import React, { useMemo } from "react";
import Select from "react-select";
import "../../styles/common/dashboardCourseSection.scss";

import ChartCard from "./charts/ChartCard";
import DoughnutChart from "./charts/DoughnutChart";
import BarChart from "./charts/BarChart";

const DashboardBatchSection = ({
  batchOptions,
  selectedBatchOption,
  onChangeBatch,
  batches,
  batchData,
  showSelectRow = true,
}) => {
  const chartPalette = useMemo(
    () => ({
      solid: {
        orange: "rgba(252, 164, 59, 0.85)",
        mint: "rgba(133, 238, 173, 0.85)",
        lime: "rgba(212, 249, 45, 0.85)",
        yellow: "rgba(250, 238, 112, 0.85)",
        slate: "rgba(148, 163, 184, 0.65)",
      },
      hover: {
        orange: "rgba(252, 164, 59, 0.95)",
        mint: "rgba(133, 238, 173, 0.95)",
        lime: "rgba(212, 249, 45, 0.95)",
        yellow: "rgba(250, 238, 112, 0.95)",
        slate: "rgba(148, 163, 184, 0.85)",
      },
    }),
    [],
  );

  const safeCounts = batchData?.counts || {};
  const safePages = batchData?.pages || {};

  const subjectPagesCompleted = safePages?.subject_pages?.completed ?? 0;
  const subjectPagesTotal = safePages?.subject_pages?.total ?? 0;
  const testsCompleted = safePages?.tests?.completed ?? 0;
  const testsTotal = safePages?.tests?.total ?? 0;
  const quizzesCompleted = safePages?.quizzes?.completed ?? 0;
  const quizzesTotal = safePages?.quizzes?.total ?? 0;

  const subjectPagesRemaining = Math.max(
    (subjectPagesTotal ?? 0) - (subjectPagesCompleted ?? 0),
    0,
  );
  const testsRemaining = Math.max((testsTotal ?? 0) - (testsCompleted ?? 0), 0);
  const quizzesRemaining = Math.max(
    (quizzesTotal ?? 0) - (quizzesCompleted ?? 0),
    0,
  );

  return (
    <section className="dashboard-course-section">
      <h2>Batch</h2>
      {showSelectRow && (
        <div className="course-select-row">
          <label>Select batch:</label>
          <div className="course-select-wrapper">
            <Select
              value={selectedBatchOption}
              onChange={onChangeBatch}
              options={batchOptions}
              isDisabled={batches.length === 0}
              isClearable={false}
            />
          </div>
        </div>
      )}

      <div className="course-metrics-grid">
        <div className="card metric-card">
          <div className="metric-label">Total Users</div>
          <div className="metric-value">{safeCounts.total_users ?? 0}</div>
        </div>
        <div className="card metric-card">
          <div className="metric-label">Active Users</div>
          <div className="metric-value">{safeCounts.active_users ?? 0}</div>
        </div>
        <div className="card metric-card">
          <div className="metric-label">Inactive Users</div>
          <div className="metric-value">{safeCounts.inactive_users ?? 0}</div>
        </div>
        <div className="card metric-card">
          <div className="metric-label">Total Subjects</div>
          <div className="metric-value">{safeCounts.total_subjects ?? 0}</div>
        </div>
        <div className="card metric-card">
          <div className="metric-label">Total Teachers</div>
          <div className="metric-value">{safeCounts.total_teachers ?? 0}</div>
        </div>
      </div>

      <div className="dashboard-course-grid">
        <ChartCard title="Users Distribution">
          <DoughnutChart
            data={{
              labels: ["Active", "Inactive"],
              datasets: [
                {
                  data: [
                    safeCounts.active_users || 0,
                    safeCounts.inactive_users || 0,
                  ],
                  backgroundColor: [
                    chartPalette.solid.mint,
                    chartPalette.solid.slate,
                  ],
                  hoverBackgroundColor: [
                    chartPalette.hover.mint,
                    chartPalette.hover.slate,
                  ],
                  borderColor: "rgba(15, 23, 42, 1)",
                  borderWidth: 0,
                },
              ],
            }}
          />
        </ChartCard>

        <ChartCard title="Pages Completion">
          <BarChart
            data={{
              labels: ["Subject Pages", "Tests", "Quizzes"],
              datasets: [
                {
                  label: "Completed",
                  data: [
                    subjectPagesCompleted || 0,
                    testsCompleted || 0,
                    quizzesCompleted || 0,
                  ],
                  backgroundColor: chartPalette.solid.lime,
                  hoverBackgroundColor: chartPalette.hover.lime,
                  borderRadius: 10,
                },
                {
                  label: "Remaining",
                  data: [
                    subjectPagesRemaining,
                    testsRemaining,
                    quizzesRemaining,
                  ],
                  backgroundColor: chartPalette.solid.orange,
                  hoverBackgroundColor: chartPalette.hover.orange,
                  borderRadius: 10,
                },
              ],
            }}
            options={{
              responsive: true,
              plugins: { legend: { position: "bottom" } },
              scales: {
                x: { stacked: true },
                y: { stacked: true, beginAtZero: true },
              },
            }}
          />
        </ChartCard>
      </div>
    </section>
  );
};

export default DashboardBatchSection;
